// Step 1: the DFT data - choose the uploaded VASP / Quantum ESPRESSO files, or import a run of a DFT project, read them,
// look at the bands. DftViews (bands and lattice) is also the DFT module's result view.
import { Alert, Anchor, Badge, Button, FileButton, Grid, Group, NumberInput, ScrollArea, SegmentedControl, Select, Stack, Table, Tabs,
  Text, TextInput } from "@mantine/core";
import { useDebouncedValue, useMediaQuery } from "@mantine/hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { ErrorBox, notifyError } from "../../components/Errors";
import { CancelButton, JobProgress } from "../../components/Jobs";
import { Plot } from "../../components/Plot";
import { bandFigure } from "../../components/SkfitterPlots";
import { formatBytes, formatDate, post, uploadFile, type Job, type ProjectDetail } from "../../lib/api";
import { dftRuns, importDftRun, type DftRun } from "../../lib/qe";
import { bandView, guessFiles, type DftResult, type SkForms, type SkMemory } from "../../lib/skfitter";
import { StructureTab } from "./LatticeViews";

type ReadInput = { code: string; files: Record<string, string>; seed: string; extra?: Partial<SkMemory> };

const COLOR_BY = [
  { value: "family", label: "Orbital family (s / p / d)" }, { value: "element", label: "Element" },
  { value: "orbital", label: "Single orbital" }, { value: "none", label: "No colouring" },
];
const SPIN = [
  { value: "side", label: "Side by side" }, { value: "overlay", label: "Overlaid" }, { value: "up", label: "Spin up only" },
  { value: "down", label: "Spin down only" },
];

export function DftStep({ project, forms, mem, update, remember, dftJob, onNext }: {
  project: ProjectDetail; forms: SkForms; mem: SkMemory; update: (m: Partial<SkMemory>) => void;
  remember: (m: Partial<SkMemory>) => void; dftJob: Job | undefined; onNext: () => void;
}) {
  const qc = useQueryClient();
  const roles = forms.files[mem.code] ?? [];
  // uploads, and copies of a DFT run's files ("Import from a DFT project")
  const uploads = useMemo(() => project.files.filter((f) => (f.kind === "input" || f.kind === "copy") && f.status === "ready"),
    [project.files]);
  const options = uploads.map((f) => ({ value: f.id, label: `${f.name}  (${formatBytes(f.size)})` }));

  // fill the file fields from the names of the uploaded files, as the desktop fills them from a folder
  useEffect(() => {
    const next = guessFiles(roles, uploads, mem.files);
    if (JSON.stringify(next) !== JSON.stringify(mem.files)) update({ files: next });
  }, [roles, uploads, mem.files, update]);

  const [uploading, setUploading] = useState(false);
  const uploadAll = async (files: File[]) => {
    setUploading(true);
    try {
      for (const f of files) await uploadFile(project.id, f);
    } catch (e) {
      notifyError(e);
    } finally {
      setUploading(false);
      qc.invalidateQueries({ queryKey: ["project", project.id] });
      qc.invalidateQueries({ queryKey: ["me"] });
    }
  };

  const missing = roles.filter((r) => r.required && !mem.files[r.role]);
  const read = useMutation({
    mutationFn: (v: ReadInput) => post<Job>("/jobs", { kind: "skfitter.dft", project_id: project.id,
      params: { dft: { code: v.code, files: v.files }, seed: v.seed } }),
    onSuccess: (j, v) => {
      remember({ dft_job: j.id, code: v.code, files: v.files, seed: v.seed, auto_read: false, ...(v.extra ?? {}) });
      qc.invalidateQueries({ queryKey: ["project", project.id] });
    },
  });
  const readNow = () => read.mutate({ code: mem.code, files: mem.files, seed: mem.seed });
  // a project made with the DFT module's "Use in SKFitter" holds the run's files: read them once, straight away
  const autoStarted = useRef(false);
  useEffect(() => {
    if (mem.auto_read && !mem.dft_job && !autoStarted.current && Object.keys(mem.files).length) {
      autoStarted.current = true;
      read.mutate({ code: mem.code, files: mem.files, seed: mem.seed });
    }
  }, [mem.auto_read, mem.dft_job, mem.code, mem.files, mem.seed, read]);
  const busy = dftJob && (dftJob.status === "queued" || dftJob.status === "running");
  const dft = dftJob?.status === "succeeded" ? (dftJob.result as DftResult) : undefined;
  const fromDft = mem.dft_source === "import" || mem.dft_source === "generate";
  const phone = useMediaQuery("(max-width: 36em)");
  // VASP / Quantum ESPRESSO files, or a run of one of the user's DFT projects
  const sourceControl = (
    <SegmentedControl value={fromDft ? "import" : mem.code} orientation={phone ? "vertical" : "horizontal"} fullWidth={!phone}
      data={[...forms.codes.map((c) => ({ ...c, label: `${c.label} files` })), { value: "import", label: "Import from a DFT project" }]}
      onChange={(v) => (v === "import" ? update({ dft_source: "import" }) : update({ dft_source: "upload", code: v, files: {} }))} />
  );
  const progress = dftJob && busy && (
    <Stack gap={4}>
      <JobProgress job={dftJob} />
      <Group justify="flex-end"><CancelButton job={dftJob} /></Group>
    </Stack>
  );
  if (fromDft) {
    return (
      <Stack gap={0} mt="md">
        {sourceControl}
        <Grid gutter="lg" mt="md">
          <Grid.Col span={{ base: 12, lg: 5 }}>
            <Stack gap="sm">
              {mem.dft_source === "generate" && dft && (
                <Alert color="blue" p="xs"><Text size="xs">These DFT data were made with the fit's former "Generate with Quantum
                  ESPRESSO". Quantum ESPRESSO calculations are now a module of their own: DFT.</Text></Alert>
              )}
              <DftImport busy={!!busy || read.isPending}
                onImport={(run) => importDftRun(project.id, run.job_id).then((r) => {
                  qc.invalidateQueries({ queryKey: ["project", project.id] });
                  qc.invalidateQueries({ queryKey: ["me"] });
                  read.mutate({ code: r.code, files: r.files, seed: r.seed,
                    extra: { dft_source: "upload", imported_from: { project_id: run.project_id, job_id: run.job_id } } });
                })} />
              <ErrorBox error={read.error} />
              {progress}
              {dftJob?.error && <ErrorBox error={new Error(dftJob.error)} />}
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 7 }}>
            {dft ? <DftViews jobId={dftJob!.id} dft={dft} onNext={onNext} />
              : <Text c="dimmed" mt="md">The band structure and the lattice appear here once the run's data has been read.</Text>}
          </Grid.Col>
        </Grid>
      </Stack>
    );
  }

  return (
    <Stack gap={0} mt="md">
    {sourceControl}
    <Grid gutter="lg" mt="md">
      <Grid.Col span={{ base: 12, lg: 4 }}>
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            {mem.imported_from && (
            <Alert color="blue" p="xs" data-testid="imported-from"><Text size="xs">These Quantum ESPRESSO files are copies from a
              run of your DFT project{" "}<Anchor component={Link} to={`/projects/${mem.imported_from.project_id}`} size="xs">(open it)</Anchor>.
            </Text></Alert>
          )}
          {mem.code === "espresso"
              ? "Choose the Quantum ESPRESSO files of the bands run: the pw.x XML data file (required), its input file for the k-path labels, and optionally the SCF output (Fermi energy) and the projwfc.x projections."
              : "Choose the two VASP files of the band-structure run: vasprun.xml (LORBIT = 11 for orbital projections) and the line-mode KPOINTS file."}
            {" "}Upload them here or in Files below.
          </Text>
          {roles.map((r) => (
            <Select key={r.role} label={<>{r.label} {r.required ? <Badge size="xs" variant="light">required</Badge> : <Text span size="xs" c="dimmed">optional</Text>}</>}
              placeholder={uploads.length ? "Choose an uploaded file" : "Upload the file first"} data={options} value={mem.files[r.role] ?? null}
              clearable searchable nothingFoundMessage="No such file" aria-label={r.label}
              onChange={(v) => update({ files: Object.fromEntries(Object.entries({ ...mem.files, [r.role]: v ?? "" }).filter(([, x]) => x)) })} />
          ))}
          <FileButton onChange={(fs) => fs.length && uploadAll(fs)} multiple>
            {(props) => <Button variant="default" size="xs" {...props} loading={uploading}>Upload DFT files…</Button>}
          </FileButton>
          <TextInput label="Seedname" description="Output files are called <seedname>_fit.json, <seedname>_hr.dat, …"
            value={mem.seed} onChange={(e) => update({ seed: e.currentTarget.value })} />
          <ErrorBox error={read.error} />
          <Button onClick={readNow} loading={read.isPending} disabled={!!missing.length || !mem.seed.trim() || !!busy}>
            {dft ? "Read the DFT data again" : "Read the DFT data"}
          </Button>
          {missing.length > 0 && <Text size="xs" c="dimmed">Choose the {missing.map((r) => r.label).join(" and ")} file first.</Text>}
          {progress}
          {dftJob?.error && <ErrorBox error={new Error(dftJob.error)} />}
        </Stack>
      </Grid.Col>
      <Grid.Col span={{ base: 12, lg: 8 }}>
        {dft ? <DftViews jobId={dftJob!.id} dft={dft} onNext={onNext} />
          : <Text c="dimmed" mt="md">The band structure and the lattice appear here once the DFT data has been read.</Text>}
      </Grid.Col>
    </Grid>
    </Stack>
  );
}

/** The finished runs of the user's DFT projects, to import one (copies of its files, then read like uploads). */
function DftImport({ busy, onImport }: { busy: boolean; onImport: (run: DftRun) => Promise<void> }) {
  const runs = useQuery({ queryKey: ["dft-runs"], queryFn: dftRuns });
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const take = (run: DftRun) => {
    setPending(run.job_id); setError(null);
    onImport(run).catch(setError).finally(() => setPending(null));
  };
  const list = runs.data ?? [];
  return (
    <Stack gap="xs" data-testid="dft-import">
      <Text size="sm" c="dimmed">The band-structure runs of your DFT projects. Importing one copies its Quantum ESPRESSO files into
        this project and reads them, as if you had uploaded them.</Text>
      <ErrorBox error={runs.error ?? error} />
      {runs.isSuccess && list.length === 0 && (
        <Alert color="gray" p="xs"><Text size="sm">No finished DFT runs yet. Create a project of the kind "DFT (Quantum ESPRESSO)",
          run a calculation there, and import it here.</Text></Alert>
      )}
      {list.length > 0 && (
        <ScrollArea.Autosize mah={360}>
          <Table striped highlightOnHover fz="xs" verticalSpacing={4}>
            <Table.Thead><Table.Tr><Table.Th>DFT project</Table.Th><Table.Th>Material</Table.Th><Table.Th>Data</Table.Th>
              <Table.Th>Gap</Table.Th><Table.Th>Run</Table.Th><Table.Th /></Table.Tr></Table.Thead>
            <Table.Tbody>
              {list.map((r) => (
                <Table.Tr key={r.job_id}>
                  <Table.Td>{r.project_name}</Table.Td>
                  <Table.Td>{[r.formula, r.recipe].filter(Boolean).join(" · ")}</Table.Td>
                  <Table.Td>{r.kpoints} k × {r.bands} bands{r.soc ? " · SOC" : ""}{r.magnetic ? " · magnetic" : ""}
                    {r.has_projection ? "" : " · no projections"}</Table.Td>
                  <Table.Td>{r.gap_eV == null ? "-" : `${r.gap_eV.toFixed(3)} eV`}</Table.Td>
                  <Table.Td>{r.created_at ? formatDate(r.created_at) : ""}</Table.Td>
                  <Table.Td><Button size="compact-xs" onClick={() => take(r)} loading={pending === r.job_id}
                    disabled={busy || (pending !== null && pending !== r.job_id)}>Import</Button></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea.Autosize>
      )}
    </Stack>
  );
}

/** The DFT data: its band structure, and the lattice it was calculated for. ``action`` replaces "Next: fit set-up"
 * (the DFT module puts "Use in SKFitter" there); ``lattice`` replaces SKFitter's lattice view (the DFT module's own);
 * ``extraTabs`` adds tabs after the bands (the DFT module's density of states). */
export function DftViews({ jobId, dft, onNext, action, lattice, extraTabs }: {
  jobId: string; dft: DftResult; onNext?: () => void; action?: ReactNode; lattice?: ReactNode;
  extraTabs?: { value: string; label: string; node: ReactNode }[];
}) {
  const [tab, setTab] = useState<string | null>("bands");
  return (
    <Stack gap="xs">
      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="bands">Band structure</Tabs.Tab>
          {extraTabs?.map((t) => <Tabs.Tab key={t.value} value={t.value}>{t.label}</Tabs.Tab>)}
          <Tabs.Tab value="lattice">Lattice structure</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="bands" pt="xs"><Bands jobId={jobId} dft={dft} /></Tabs.Panel>
        {extraTabs?.map((t) => <Tabs.Panel key={t.value} value={t.value} pt="xs">{t.node}</Tabs.Panel>)}
        <Tabs.Panel value="lattice" pt="xs">{lattice ?? <StructureTab jobId={jobId} />}</Tabs.Panel>
      </Tabs>
      <Group justify="flex-end">{action ?? (onNext && <Button onClick={onNext}>Next: fit set-up →</Button>)}</Group>
    </Stack>
  );
}

function Bands({ jobId, dft }: { jobId: string; dft: DftResult }) {
  const m = dft.meta;
  const [opts, setOpts] = useState({ color_by: m.has_projection ? "family" : "none", atoms: "All", orbital: "s", emin: -6, emax: 6, spin_view: "side" });
  const [debounced] = useDebouncedValue(opts, 250);
  const view = useQuery({ queryKey: ["sk-bands", jobId, debounced], queryFn: () => bandView({ job_id: jobId, ...debounced }),
    placeholderData: (p) => p });
  const fig = useMemo(() => (view.data ? bandFigure(view.data) : null), [view.data]);
  const orbitals = [...Object.keys(m.family_cols).map((f) => ({ value: f, label: `${f} (all)` })),
    ...m.orbital_names.filter((n) => !(n in m.family_cols)).map((n) => ({ value: n, label: n }))];
  const flags = [`E_F = ${m.efermi.toFixed(3)} eV`, `${m.nk} k-points`, `${m.nbands} bands`, m.soc ? "SOC" : "no SOC",
    m.magnetic ? "spin-polarised" : "spin-degenerate", m.has_projection ? "orbital projections" : "no projections"];
  const set = (p: Partial<typeof opts>) => setOpts((o) => ({ ...o, ...p }));
  return (
    <Stack gap="xs">
      <Text size="sm">{[m.formula, m.spacegroup, dft.code_label, ...flags].join("  ·  ")}</Text>
      <Group gap="xs" align="flex-end" wrap="wrap">
        <Select size="xs" w={200} label="Colour by" data={COLOR_BY} value={opts.color_by} allowDeselect={false}
          disabled={!m.has_projection} onChange={(v) => v && set({ color_by: v })} />
        <Select size="xs" w={90} label="Atoms" data={["All", ...m.elements]} value={opts.atoms} allowDeselect={false}
          disabled={!m.has_projection || !["family", "orbital"].includes(opts.color_by)} onChange={(v) => v && set({ atoms: v })} />
        <Select size="xs" w={110} label="Orbital" data={orbitals} value={opts.orbital} allowDeselect={false}
          disabled={!m.has_projection || opts.color_by !== "orbital"} onChange={(v) => v && set({ orbital: v })} />
        {m.magnetic && <Select size="xs" w={140} label="Spin" data={SPIN} value={opts.spin_view} allowDeselect={false}
          onChange={(v) => v && set({ spin_view: v })} />}
        <NumberInput size="xs" w={90} label="E min (eV)" value={opts.emin} step={0.5} min={-60} max={0}
          onChange={(v) => typeof v === "number" && set({ emin: v })} />
        <NumberInput size="xs" w={90} label="E max (eV)" value={opts.emax} step={0.5} min={0} max={60}
          onChange={(v) => typeof v === "number" && set({ emax: v })} />
      </Group>
      <ErrorBox error={view.error} />
      {fig && <Plot data={fig.data} layout={fig.layout} height={460} />}
      {dft.warnings.map((w) => <Alert key={w} color="orange" p="xs"><Text size="xs">{w}</Text></Alert>)}
    </Stack>
  );
}
